import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Gift, Clock, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Promotional Banner Component
 * Shows welcome message for users on promotional plan
 * Dismissible and persists dismissal state in localStorage
 */
const PromotionalBanner = ({ subscription, userName }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if user has promotional subscription and hasn't dismissed the banner
    const dismissed = localStorage.getItem('promotional_banner_dismissed');
    const isPromotional = subscription?.isPromotional === true;
    
    if (isPromotional && !dismissed) {
      setIsVisible(true);
    }
  }, [subscription]);

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
    localStorage.setItem('promotional_banner_dismissed', 'true');
  };

  // Hardcoded promotional message - ignores cached subscription message to ensure consistency
  const message = "🎉 You're on a free 3-month promotional plan until 31 May 2026. Dive into your intelligent learning journey and enjoy access to several premium features. Let's get started!";

  // Calculate days remaining
  const endDate = subscription?.endDate ? new Date(subscription.endDate) : new Date('2026-05-31');
  const today = new Date();
  const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));

  if (!isVisible || isDismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="relative overflow-hidden"
        data-testid="promotional-banner"
      >
        {/* Gradient Background */}
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 rounded-xl p-4 md:p-5 shadow-lg border border-violet-400/20">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-2xl -mr-16 -mt-16" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-pink-500/10 rounded-full blur-xl -ml-12 -mb-12" />
          
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            {/* Icon and Message */}
            <div className="flex items-start gap-3 flex-1">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Gift className="w-5 h-5 text-yellow-300" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-yellow-300" />
                  <span className="text-sm font-semibold text-yellow-300 uppercase tracking-wide">
                    Promotional Offer
                  </span>
                </div>
                <p className="text-white text-sm md:text-base leading-relaxed">
                  {message}
                </p>
                {daysRemaining > 0 && (
                  <div className="flex items-center gap-1 mt-2 text-violet-200 text-xs">
                    <Clock className="w-3 h-3" />
                    <span>{daysRemaining} days remaining</span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="text-white/70 hover:text-white hover:bg-white/10"
                data-testid="dismiss-promotional-banner"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Premium Features Teaser */}
          <div className="relative mt-4 pt-4 border-t border-white/10">
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-violet-200">Included:</span>
              {["Ed's Tutor", 'Practice Papers', 'Diagnostic', 'Learning Path', 'ELibrary', '1 Mock Exam', '10 Challenger Qs'].map((feature) => (
                <span
                  key={feature}
                  className="px-2 py-1 text-xs bg-white/10 text-white rounded-full"
                >
                  {feature}
                </span>
              ))}
              <span className="px-2 py-1 text-xs bg-yellow-500/20 text-yellow-300 rounded-full flex items-center gap-1">
                <ChevronRight className="w-3 h-3" />
                Upgrade for more
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PromotionalBanner;
